import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Popover,
  TextField,
  Typography
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/app/hooks';
import { ILanguage } from 'src/models/general';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PublicIcon from '@mui/icons-material/Public';
import { Theme } from '@mui/material';
import { SxProps } from '@mui/system';

interface IProps {
  languages: ILanguage[];
  onSubmit: (languages: ILanguage[]) => void;
  buttonSx?: SxProps<Theme>;
}

const SelectMultipleLanguage = ({ languages, onSubmit, buttonSx }: IProps) => {
  const { t } = useTranslation();
  const languageOptions: ILanguage[] = useMemo(
    () => [{ name: t('all languages'), language: 'all' }, ...languages],
    [languages, t]
  );
  const { filterLanguages } = useAppSelector(
    (state) => state.storeOnlineMediaStream
  );

  const [selectedLanguages, setSelectedLanguages] =
    useState<ILanguage[]>(filterLanguages);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedLanguages(filterLanguages);
  };

  const handleApply = () => {
    onSubmit(selectedLanguages);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'select-language-popover' : undefined;

  return (
    <>
      <Button
        aria-describedby={id}
        variant="outlined"
        startIcon={<PublicIcon />}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          fontSize: { xs: 11, sm: 14 },
          ...buttonSx
        }}
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
      >
        {filterLanguages.length <= 0
          ? t('all languages')
          : filterLanguages.length === 1
          ? filterLanguages[0].name
          : `+${filterLanguages.length} ${t('languages')}`}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Paper
          sx={{
            px: 2,
            py: 2,
            width: { xs: '300px', md: '400px' },
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          <Autocomplete
            fullWidth
            multiple
            size="small"
            sx={{
              '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                backgroundColor: '#FFFFFF'
              }
            }}
            disableCloseOnSelect
            options={languageOptions}
            getOptionLabel={(option: any) => option.name}
            ListboxProps={{ style: { padding: 0 } }}
            value={selectedLanguages}
            isOptionEqualToValue={(option: any, value: any) =>
              option.language === value.language
            }
            onChange={(_: React.SyntheticEvent, newValue: any) => {
              const hasAllOption = newValue.some(
                (lang: any) => lang.language === 'all'
              );

              if (hasAllOption) {
                const allSelected =
                  selectedLanguages.length === languages.length;
                setSelectedLanguages(allSelected ? [] : languages);
              } else {
                setSelectedLanguages(newValue);
              }
            }}
            renderInput={(params: any) => (
              <TextField
                fullWidth
                placeholder={
                  selectedLanguages.length <= 0 ? t('all languages') : ''
                }
                {...params}
              />
            )}
            renderTags={(values: any[], getTagProps) => {
              if (values.length > 6) {
                return (
                  <>
                    {values.slice(0, 6).map((option: any, index: number) => (
                      <Chip
                        key={`${option.language}-${index}-includes`}
                        variant="outlined"
                        label={<Typography>{option.name}</Typography>}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))}
                    <Chip
                      key={`rest`}
                      variant="outlined"
                      label={<Typography>+{values.length - 6}</Typography>}
                      size="small"
                    />
                  </>
                );
              }
              return values.map((option: any, index: number) => (
                <Chip
                  key={`${option.language}-${index}-includes`}
                  variant="outlined"
                  label={<Typography>{option.name}</Typography>}
                  size="small"
                  {...getTagProps({ index })}
                />
              ));
            }}
            renderOption={(props: any, option: any, { selected }) => {
              const isIndeterminate =
                selectedLanguages.length > 0 &&
                selectedLanguages.length !== languages.length;

              const isChecked =
                option.language !== 'all'
                  ? selected
                  : selectedLanguages.length === languages.length;

              const handleChange = (
                e: React.ChangeEvent<HTMLInputElement>,
                checked: boolean
              ) => {
                if (option.language === 'all') {
                  if (checked) setSelectedLanguages(languages);
                  else setSelectedLanguages([]);
                }
              };

              return (
                <li key={option.language} {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={isChecked}
                    onChange={handleChange}
                    indeterminate={option.language === 'all' && isIndeterminate}
                  />
                  {option.language === 'all' ? t('all languages') : option.name}
                </li>
              );
            }}
          />
          <Box textAlign="right">
            <Button variant="contained" onClick={() => handleApply()}>
              {t('apply')}
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default SelectMultipleLanguage;
